import { enquiryDraft } from '@/lib/enquiry';
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { FadeIn } from './animations/FadeIn';

const ContactFloatingButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    message: '',
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(()=>{
    if(!isOpen)return;
    const previous=document.activeElement as HTMLElement|null;
    const overflow=document.body.style.overflow;document.body.style.overflow="hidden";
    dialogRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKey=(event:KeyboardEvent)=>{
      if(event.key==="Escape")setIsOpen(false);
      if(event.key!=="Tab")return;
      const items=Array.from(dialogRef.current?.querySelectorAll<HTMLElement>("button,a[href],input,textarea")||[]).filter(e=>e.getClientRects().length>0);
      const first=items[0],last=items.at(-1);
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
    };
    document.addEventListener("keydown",onKey);
    return()=>{document.body.style.overflow=overflow;document.removeEventListener("keydown",onKey);previous?.focus();};
  },[isOpen,isSubmitted]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-5 right-5 z-40 sm:bottom-6 sm:right-6">
        <button type="button" onClick={()=>setIsOpen(true)} aria-label="Contact the school" aria-expanded={isOpen} aria-controls="contact-dialog" className="flex items-center gap-2 rounded-full bg-[#101c32] border border-white/20 text-white px-4 py-3.5 shadow-xl hover:bg-[#213650] transition-colors">
          <MessageCircle size={22} aria-hidden/><span className="hidden sm:inline text-sm font-semibold">Get in touch</span>
        </button>
      </div>

      {/* Modal / Bottom Sheet */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isSubmitted && setIsOpen(false)}
          >
            <motion.div
              ref={dialogRef} id="contact-dialog" role="dialog" aria-modal="true" aria-label="Contact the school"
              className="bg-white border border-slate-200 w-full sm:max-w-xl relative overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] rounded-t-[2rem] sm:rounded-[2.5rem] max-h-[92dvh] overflow-y-auto"
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* drag handle on mobile */}
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-10 h-1 bg-foreground/20 rounded-full" />
              </div>

              <div className="p-6 sm:p-10">
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close contact form" type="button"
                  className="absolute top-5 right-5 sm:top-8 sm:right-8 text-slate-600 hover:text-primary transition-colors"
                >
                  <X className="w-6 h-6 sm:w-7 sm:h-7" />
                </button>

                {isSubmitted ? (
                  <FadeIn className="text-center py-10 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-8 h-8 sm:w-9 sm:h-9 text-primary" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-foreground mb-3 tracking-tight">
                      Your email draft is ready
                    </h3>
                    <p className="text-base sm:text-lg text-muted-foreground font-medium">
                      Send your prepared message from your email app to complete your enquiry.
                    </p>
                    <a href={enquiryDraft({name:formData.fullName,email:formData.email,message:formData.message,phone:formData.phoneNumber})} className="inline-flex rounded-xl bg-[#101c32] px-6 py-3 text-white font-semibold mt-6">Open email app</a>
                    <button type="button" onClick={()=>setIsSubmitted(false)} className="block mx-auto mt-4 text-sm font-semibold text-slate-700 underline">Edit message</button>
                  </FadeIn>
                ) : (
                  <div className="space-y-6 sm:space-y-8">
                    <div>
                      <span className="text-[10px] font-black tracking-[0.2em] uppercase text-primary mb-1 block">
                        Connect
                      </span>
                      <h2 className="text-3xl sm:text-4xl font-black text-foreground tracking-tighter">
                        Let's <span className="text-gradient">Talk</span>
                      </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <p className="text-sm text-slate-600">We’ll prepare an email draft for you to send to the school.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label htmlFor="contact-fullName" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1 block">
                            Full Name
                          </label>
                          <input
                            type="text"
                            id="contact-fullName" name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-foreground/5 border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-bold placeholder:text-slate-500 text-base"
                            placeholder="Your name"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label htmlFor="contact-email" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1 block">
                            Email
                          </label>
                          <input
                            type="email"
                            id="contact-email" name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-foreground/5 border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-bold placeholder:text-slate-500 text-base"
                            placeholder="your@email.com"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="contact-message" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1 block">
                          Your Enquiry
                        </label>
                        <textarea
                          id="contact-message" name="message"
                          rows={3}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-foreground/5 border-none rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-bold placeholder:text-slate-500 resize-none text-base"
                          placeholder="What's on your mind?"
                        />
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className="w-full py-4 bg-foreground text-background rounded-xl font-black text-base shadow-xl hover:shadow-2xl transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                      >
                        Prepare Email
                        <Send size={18} aria-hidden />
                      </motion.button>
                    </form>

                    <div className="flex justify-between items-center pt-5 border-t border-foreground/5">
                      <div className="flex gap-3">
                        {[Phone, MapPin].map((Icon, i) => (
                          <div
                            key={i}
                            className="w-9 h-9 bg-primary/5 rounded-xl flex items-center justify-center text-primary"
                          >
                            <Icon size={16} />
                          </div>
                        ))}
                      </div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                        Renaissance Garden Schools
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContactFloatingButton;
